/* eslint no-console: ["error", { allow: ["log", "warn", "error"] }] */
import imm from 'immutable';
import {
    BESIDA_ROLE_COMPANY,
    CONTEXT_TYPE_PRODUCT,
    CONTEXT_TYPE_ORDER,
    MESSAGE_STATUS_READ,
    MESSAGE_STATUS_SENDING,
    MESSAGE_STATUS_DELIVERED,
    MESSAGE_STATUS_NOT_DELIVERED,
    getCurrentUserIdent,
    getUserRoleInRoom,
    getRoom
} from '@evo/chat-core';

import * as c from './constants';
import { Config } from '../core/config';

export function delay(time) {
    return function delayWrapper() {
        return this.delay(time);
    };
}

export function isEmpty(obj) {
    if (!obj) return true;

    if (typeof obj === 'string' || Array.isArray(obj)) return obj.length === 0;
    if ((obj).constructor.name !== 'Object') return true;

    for(const prop in obj) { // eslint-disable-line
        if(obj.hasOwnProperty(prop))
            return false;
    }

    return JSON.stringify(obj) === JSON.stringify({});
}

export function isNaN(subject) {
    if (subject === 'NaN') return false;

    return `${subject}` === 'NaN';
}

export function flatten(arr) {
  return arr.reduce((flat, toFlatten) => {
    return flat.concat(toFlatten);
  }, []);
}

export function objectValues(obj) {
    if (isEmpty(obj)) return [];

    return Object.values(obj);
}

export function findObjInArr(arrObjects, func) {
    if (!arrObjects) return {};

    const foundObj = arrObjects.find(func);

    return foundObj || {};
}

export function range(number) {
    const rangeArr = [];

    if (!number) return rangeArr;

    for (let i = 0; i < number; i++) {
        rangeArr.push(i);
    }

    return rangeArr;
}

export function isFunction(subject) {
    return !!subject && (subject).constructor.name === 'Function';
}

export function collectDispatchers(...dispatchers) {
    return flatten(dispatchers.map((handler) => handler.collect()));
}

export function getSocketStore(state) {
    return (state.get('besida') && state.get('besida').store) || imm.Map({});
}

export function getChatFromViewStore(state, roomIdent) {
    return state.getIn(['socketViewStore', 'chats', roomIdent]) || imm.fromJS({
        history: [],
    });
}

export function countInputRows(inputString) {
    const breaksMatches = inputString.match(/\n/gi);
    const breaksTotal = breaksMatches && breaksMatches.length;

    if (breaksTotal <= 3) return 3;
    return breaksTotal > 7 ? 7 : breaksTotal;
}

export function storeChatsLocally(userId, chats) {
    if (!chats.length) return void 0;
    userId = userId || 'anon';
    let storedChats;
    const chatsId = `${c.CHATS_STORAGE_PREFIX}${userId}`;
    try {
        storedChats = JSON.parse(localStorage.getItem(chatsId)) || {};
    } catch (e) {
        storedChats = {};
    }
    chats.forEach((i) => {
        storedChats[i.room_ident] = i.history;
    });
    try {
        localStorage.setItem(chatsId, JSON.stringify(storedChats));
    } catch (e) {
        console.error('Cannot set item to LocalStorage', e);
    }
    return void 0;
}

export function getParamByName(paramName, url = location.search) {
    const name = paramName.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regexS = `[\\?&]${name}=([^&#]*)`;
    const regex = new RegExp(regexS);
    const results = regex.exec(url);
    if (results) {
        return decodeURIComponent(results[1].replace(/\+/g, ' '));
    }
    return '';
}

export function addGetParam(url, key, value) {
    const param = value ? `${key}=${value}` : key;
    const paramSeparator = url.indexOf('?') !== -1 ? '&' : '?';
    return `${url}${paramSeparator}${param}`;
}

export function newMsgCondition(chat) {
    chat = imm.Iterable.isIterable(chat) ? chat : imm.fromJS(chat);
    return chat.get('last_message_counter') > chat.get('last_read_counter') && !chat.get('noMsgSent');
}

export function excludeExecProtocol(rawString) {
    const result = rawString.replace(/^(javascript|vbscript|data):/, '');
    const wasModified = result !== rawString;
    return wasModified ? excludeExecProtocol(result) : result;
}

export function sanitizeLocation(rawString) {
    return excludeExecProtocol(rawString);
}

export function eachRightMsg(chatHistory, callBack, iterTo = 1) {
    iterTo = iterTo < 0 ? 0 : iterTo;
    let i = chatHistory.length - 1;
    for (i; i >= iterTo; i--) {
        if (callBack(chatHistory[i], i)) break;
    }
}

export function isProductCntx(msg) {
    return Boolean(msg.get('context_item_type') === CONTEXT_TYPE_PRODUCT);
}

export function findLastUnreadMsg(chatHistory, lastReadCounter) {
    let lastUnread = null;
    chatHistory = chatHistory || imm.List();
    lastReadCounter = lastReadCounter || 0;

    if (chatHistory.size <= 1) return false;

    chatHistory.reverse().forEach((msg) => {
        if (lastUnread) return false;

        const isLastUnreadMsgAndNotCntx = (
            !isProductCntx(msg) && msg.get('id') > lastReadCounter
        );
        if (isLastUnreadMsgAndNotCntx) lastUnread = msg;
        return true;
    });

    return lastUnread;
}

export function isOnEndOfScroll(dialogContainer) {
    if (!dialogContainer) return false;

    const height = dialogContainer.scrollHeight;
    const inner = dialogContainer.scrollTop;
    const offsetHeight = dialogContainer.offsetHeight;
    const outer = dialogContainer.getBoundingClientRect().top;

    const magicHeight = 200;
    return inner >= height - offsetHeight - outer - magicHeight;
}

export function cntxSearchCond(cntxId) {
    return (item) => (
        Number(cntxId) === item.get('id') &&
        item.get('context_item_type') === CONTEXT_TYPE_PRODUCT
    );
}

export function isCntxMsg(msg) {
    return Boolean(msg.get('context_item_id') && (
        [CONTEXT_TYPE_PRODUCT, CONTEXT_TYPE_ORDER].includes(msg.get('context_item_type'))));
}

export function countChatHeightCoefficient(windowHeight) {
    if (windowHeight >= c.SIZE_OPTIMAL_WINDOW_HEIGHT) return c.COEFFICIENT_CHAT_BODY_DEFAULT;
    let coefficient = (windowHeight / c.SIZE_OPTIMAL_WINDOW_HEIGHT) - 0.1;
    // for medium screens, we need this coz text area is fixed height
    if (coefficient > 0.80) coefficient = coefficient + 0.06;
    else if (coefficient < 0.80 && coefficient > 0.70) coefficient = coefficient + 0.03;
    return coefficient < c.COEFFICIENT_CHAT_BODY_MINIMAL ? (
        c.COEFFICIENT_CHAT_BODY_MINIMAL) : coefficient;
}

export function getUrlQueryObject() {
    const searchStr = location.search.substring(1);
    let searchObj = {};

    if (!searchStr) return searchObj;

    try {
        searchObj = (
            JSON.parse(`{"${decodeURI(searchStr).replace(/"/g, '\\"')
                                                .replace(/&/g, '","')
                                                .replace(/=/g, '":"')}"}`)
        );
    } catch (e) {
        console.warn('Cannot parse url search string. JSON.parse: ', e);
    }

    return searchObj;
}

export function addOrderToStore(store, orderId) {
    if (store.length > 100) localStorage.setItem(c.ORDERS_STORAGE_IDENT, JSON.stringify([orderId]));
    store.push(orderId);
    localStorage.setItem(c.ORDERS_STORAGE_IDENT, JSON.stringify(store));
}

export function sentOrdersStore() {
    let storedOrders;
    try {
        storedOrders = JSON.parse(
            localStorage.getItem(c.ORDERS_STORAGE_IDENT)
        ) || [];
    } catch (e) {
        storedOrders = [];
    }
    return storedOrders;
}

export function currentOrderId() {
    return getParamByName('order_id');
}

export function dialogToOpenFromMail(chats) {
    if (!chats.size) return false;

    const urlQueryObject = getUrlQueryObject();
    if (isEmpty(urlQueryObject)) return false;
    if (!(c.CHAT_ROOM_IDENT_MARK in urlQueryObject)) return false;

    return chats.find((chat) => chat.get('room_ident') === urlQueryObject.room_ident);
}

// improve module; remove all access to AppState
export function getPortalPageType() {
    const pageType =
        window.AppState &&
        window.AppState.modstate &&
        window.AppState.modstate.pageType;

    switch (pageType) {
        case ('product'):
            return c.ANALCS_PAGE_PRODUCT_CARD;
        case ('category'):
            return c.ANALCS_PAGE_PRODUCT_LIST;
        case ('category_list'):
            return c.ANALCS_PAGE_CATEGORY_LIST;
        case ('opinions_list'):
            return c.ANALCS_PAGE_OPINIONS_LIST;
        case ('classified_add'):
            return c.ANALCS_PAGE_CLASSIFIED_ADD;
        default:
            return null;
    }
}

export function getPageType(userRole) {
    return userRole === BESIDA_ROLE_COMPANY ? c.ANALCS_PAGE_CABINET : (
         getPortalPageType() || c.ANALCS_PAGE_HOME
    );
}

export function triggerRerenderForSocketData(state) {
    return state.set('rendTriggerForSocket', !state.get('rendTriggerForSocket'));
}

export function appendPowScript(powPath) {
    const script = document.createElement('script');
    script.src = powPath;
    document.body.append(script);
}

export function getStartButtonAnalyticsAction(payload) {
    if (!(payload && payload.node)) return null;
    const node = payload.node;
    const contactNowButton = node.dataset.noProdRemains || (
        node.parentNode && node.parentNode.dataset.noProdRemains);
    const fakeLinkButton = node.dataset.linkButton || (
        node.parentNode && node.parentNode.dataset.linkButton);
    if (contactNowButton) return c.ANALCS_ACTION_START_BUY_BUTTON;
    if (fakeLinkButton) return c.ANALCS_ACTION_START_LINK;
    return null;
}

export function getStartButtonTheme(payload) {
    if (!(payload && payload.node)) return null;
    const node = payload.node;
    return node.dataset.theme || (
        node.parentNode && node.parentNode.dataset.theme);
}

export function getStartButtonContext(payload) {
    if (!(payload && payload.node)) return null;
    const node = payload.node;
    const contextItemId = node.dataset.contextItemId || (
            node.parentNode && node.parentNode.dataset.contextItemId);
    if (!contextItemId) return null;
    return {
        contextItemId,
        contextItemType: node.dataset.contextItemType || (
            node.parentNode && node.parentNode.dataset.contextItemType),
        contextItemPrice: node.dataset.contextItemPrice || (
            node.parentNode && node.parentNode.dataset.contextItemPrice) || null,
        contextItemCurrency: node.dataset.contextItemCurrency || (
            node.parentNode && node.parentNode.dataset.contextItemCurrency) || null,
        contextDescription: node.dataset.contextDescription || (
            node.parentNode && node.parentNode.dataset.contextDescription),
        contextMeta: node.dataset.contextMeta || (
            node.parentNode && node.parentNode.dataset.contextMeta),
    };
}


export function getMessageStatus(state, msg) {
    const socketStore = getSocketStore(state);
    const userIdent = getCurrentUserIdent(socketStore);

    if (msg.get('user_ident') !== userIdent) return null;

    const currentChatRoom = getRoom(socketStore, state.get('openedRoomIdent'));
    const lastReadMessages = currentChatRoom.get('last_read_messages');
    const role = getUserRoleInRoom(userIdent, currentChatRoom);

    const isMessageRead = () => Boolean((lastReadMessages || imm.Map({})).find(
        (lastReadMsg) =>
            lastReadMsg.get('role') !== role
            && lastReadMsg.get('message_id') >= msg.get('id')));

    switch (true) {
        case msg.get('isTemporaryMessage') && !msg.get('isErrorOnSending'):
            return MESSAGE_STATUS_SENDING;
        case msg.get('isTemporaryMessage') && msg.get('isErrorOnSending'):
            return MESSAGE_STATUS_NOT_DELIVERED;
        case isMessageRead():
            return MESSAGE_STATUS_READ;
        default:
            return MESSAGE_STATUS_DELIVERED;
    }
}

export function isOppositeUserTyping(state) {
    const socketStore = getSocketStore(state);
    const userIdent = getCurrentUserIdent(socketStore);

    const currentChatRoom = getRoom(socketStore, state.get('openedRoomIdent'));
    const typing = currentChatRoom.get('typing');
    const role = getUserRoleInRoom(userIdent, currentChatRoom);

    return Boolean((typing || imm.List()).find((t) => t.get('role') !== role));
}

export function hasCompanyRole(state) {
    return (
        state.get('userHasCompany') ||
        state.get('userHasC2CCompany')
    );
}

export function getFileUrl(msg, width=null, height=null) {
    const { KOMORA_URL } = Config.data.appState;

    const withResize = !!width && !!height;

    if (msg.body) {
        try {
            return URL.createObjectURL(msg.body);
        } catch (e) {
            console.error('Failed to create object url on image: ', e);
        }
    } else if (msg.context_item_uid) {
        const fileUrl = `${KOMORA_URL}/file/${msg.context_item_uid}`;
        return withResize ? `${fileUrl}_w${width}_h${height}` : fileUrl;
    }
    return null;
}

export function changeFaviconOnMsg(newMsgInChatsPredicate) {
    const { faviconStaticUrls } = Config.data.appState;

    if (!faviconStaticUrls) return;

    const { standard, newMsg } = faviconStaticUrls;

    const favicon = document.querySelector("[type='image/x-icon']");
    if (favicon) favicon.href = newMsgInChatsPredicate ? newMsg : standard;
}